import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

import { ChallengeHomeCard } from '@fhs/loyalty/src/components/challenge-home-card';
import { ChallengeHomeCardSkeleton } from '@fhs/loyalty/src/components/challenge-home-card-skeleton';
import { OrderDisplaySkeleton, RapidRescueCard } from '@fhs/ui';
import { OrderDisplay } from '@fhs/ui/src/components/order-display';
import { OrderDisplayType } from '@fhs/ui/src/components/order-display/types';
import { useDebounce } from '@fhs-legacy/frontend/src/hooks/use-debounce';
import { useServiceModeContext } from '@fhs-legacy/frontend/src/state/service-mode';

import { ScreenHomeProps } from '../types';

type HomeCardProps = Pick<
  ScreenHomeProps,
  | 'displayedOrder'
  | 'displayedOrderCardLoading'
  | 'redirectPath'
  | 'onReorder'
  | 'reorderLoading'
  | 'logReorderCardPress'
  | 'isLoadingChallenges'
  | 'loyaltyUserReady'
  | 'isDesktop'
  | 'challengeHomeCardProps'
> & {
  noEmptyState?: boolean;
  style?: StyleProp<ViewStyle>;
};

export function HomeCard({
  style,
  displayedOrderCardLoading,
  displayedOrder,
  isLoadingChallenges,
  loyaltyUserReady,
  redirectPath,
  reorderLoading,
  onReorder,
  logReorderCardPress,
  isDesktop,
  challengeHomeCardProps,
  noEmptyState = false,
}: HomeCardProps) {
  const containerStyle = [styles.container, style];

  const isLoading = isLoadingChallenges || !loyaltyUserReady || displayedOrderCardLoading;
  const debouncedIsLoading = useDebounce(isLoading, 300);
  const { isDelivery } = useServiceModeContext();

  const shouldDisplayOrder =
    displayedOrder && displayedOrder.serviceModeTitle !== 'Delivery Order' && !isDelivery;
  const isReorder = displayedOrder && displayedOrder.type === OrderDisplayType.REORDER;

  const orderDisplay = shouldDisplayOrder ? (
    <OrderDisplay
      testID="order-display"
      type={displayedOrder.type}
      href={redirectPath}
      storeName={displayedOrder.storeName}
      items={displayedOrder.items}
      serviceModeTitle={displayedOrder.serviceModeTitle}
      reorderCallback={onReorder}
      reorderLoading={reorderLoading}
      logReorderCardPress={logReorderCardPress}
    />
  ) : null;

  if (isDesktop) {
    if (isLoading) {
      return null;
    }
    if (shouldDisplayOrder || challengeHomeCardProps) {
      return (
        <View style={[containerStyle, { gap: 32 }]}>
          {shouldDisplayOrder && orderDisplay}
          {challengeHomeCardProps && (
            <ChallengeHomeCard
              testID="challenge-home-card"
              {...challengeHomeCardProps}
              style={{ marginTop: shouldDisplayOrder ? 0 : 24 }}
            />
          )}
        </View>
      );
    }
    return null;
  }

  if (isLoadingChallenges || !loyaltyUserReady) {
    return (
      <View testID="challenge-home-skeleton" style={containerStyle}>
        <ChallengeHomeCardSkeleton />
      </View>
    );
  }

  if (debouncedIsLoading) {
    return (
      <View testID="order-display-skeleton" style={containerStyle}>
        <OrderDisplaySkeleton />
      </View>
    );
  }
  if (shouldDisplayOrder && !isReorder) {
    return <View style={containerStyle}>{orderDisplay}</View>;
  }

  if (challengeHomeCardProps) {
    return (
      <View testID="challenge-home-card" style={containerStyle}>
        <ChallengeHomeCard {...challengeHomeCardProps} />
      </View>
    );
  }

  if (shouldDisplayOrder) {
    return <View style={containerStyle}>{orderDisplay}</View>;
  }

  if (noEmptyState) {
    return null;
  }

  return (
    <View testID="rapid-rescue-card" style={[styles.rapidRescueContainer, style]}>
      <RapidRescueCard />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    paddingHorizontal: 16,
  },
  rapidRescueContainer: {
    width: '100%',
    paddingHorizontal: 16,
  },
});
